import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/tables/Icon';
import Form from '../../components/forms/Form';
import LessonDetailModal from '../students/LessonDetailModal';
import FilterBar from '../../components/settings/FilterBar';
import IconAdd from '../../components/icons/IconAdd';
import language from '../../variables/language';

const buildLessonInputs = (groupId) => ({
  start: { name: 'start', type: 'time', tagtype: 'input', value: '', disabled: false, width: '19%' },
  end: { name: 'end', type: 'time', tagtype: 'input', value: '', disabled: false, width: '19%' },
  date: { name: 'date', type: 'date', tagtype: 'input', value: '', disabled: false, width: '19%' },
  price: { name: 'price', type: 'float', step: '0.01', tagtype: 'input', value: '', disabled: false, width: '19%' },
  duration: { name: 'duration', type: 'float', step: '0.01', tagtype: 'input', value: '', disabled: false, width: '19%' },
  groupe: { name: 'groupe', value: groupId },
});

export default function GroupLessonsList({ groupId, refresh, onChange }) {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [menuLessonId, setMenuLessonId] = useState('');
  const [modalState, setModalState] = useState('none');
  const [lessonInputs] = useState(() => buildLessonInputs(groupId));

  useEffect(() => {
    axios.get('/api/lessons?groupe=' + groupId, { withCredentials: true })
      .then(response => setLessons(response.data || []))
      .catch(err => { if (err.response && err.response.status === 401) navigate('/login'); });
  }, [groupId, refresh, navigate]);

  const visibleLessons = useMemo(() => {
    const query = search.trim().toLowerCase();
    return lessons.filter(lesson => {
      if (query) {
        const haystack = ((lesson.course || '') + ' ' + (lesson.homework || '')).toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (dateFrom && lesson.date && lesson.date < dateFrom) return false;
      if (dateTo && lesson.date && lesson.date > dateTo) return false;
      return true;
    });
  }, [lessons, search, dateFrom, dateTo]);

  const stateHundler = (state, id) => {
    if (state === 'menu') {
      setMenuLessonId(id);
      setModalState('menu');
    } else if (state === 'none') {
      setModalState('none');
    }
  };

  const onModalRefresh = () => { onChange(); };

  const lang = language[language.lang];

  return (
    <div className="group-card-section">
      <div className="group-lessons-toolbar">
        <h4 className="group-card-section-title" style={{ margin: 0 }}>{lang.groupCard.lessonsTitle}</h4>
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: lang.placeholderSearch }}
          filters={[
            { key: 'dateFrom', type: 'date', label: lang.dateFrom, value: dateFrom, onChange: setDateFrom },
            { key: 'dateTo', type: 'date', label: lang.dateTo, value: dateTo, onChange: setDateTo },
          ]}
          resultCount={visibleLessons.length}
        />
        <div className="group-lessons-add-btn" onClick={() => setShowAddForm(prev => !prev)}>
          <IconAdd /> {lang.groupCard.addLessonButton}
        </div>
      </div>

      {showAddForm ? (
        <div className="group-lessons-add-panel">
          <Form inputs={lessonInputs} url="/api/lessons"
            onRefreshHundler={() => { setShowAddForm(false); onChange(); }}
            name={lang.modals.lessons.createTitle} />
        </div>
      ) : ''}

      {visibleLessons.length === 0 ? (
        <p className="group-lessons-empty">{lang.groupCard.noLessons}</p>
      ) : (
        <div className="group-lessons-table-wrap">
          <table className="table">
            <thead className="table-header">
              <tr className="table-h-tr">
                <th className="table-h-th">{lang.date}</th>
                <th className="table-h-th">{lang.start}</th>
                <th className="table-h-th">{lang.end}</th>
                <th className="table-h-th">{lang.course}</th>
                <th className="table-h-th">{lang.duration}</th>
                <th className="table-h-th">{lang.price}</th>
                <th className="table-h-th"></th>
              </tr>
            </thead>
            <tbody className="table-body">
              {visibleLessons.map(lesson => (
                <tr className="table-b-tr" key={lesson.id} style={{ cursor: 'pointer' }}
                  onClick={() => stateHundler('menu', lesson.id)}>
                  <td className="table-b-td">{lesson.date}</td>
                  <td className="table-b-td">{lesson.start}</td>
                  <td className="table-b-td">{lesson.end}</td>
                  <td className="table-b-td">{lesson.course}</td>
                  <td className="table-b-td">{lesson.duration}</td>
                  <td className="table-b-td">{lesson.price}</td>
                  <td className="table-b-td" onClick={e => e.stopPropagation()}>
                    <Icon mode="delete" url={'/api/lessons/' + lesson.id} onRefreshHundler={onChange}
                      className="icon-delete" classes="icon-dimensions-big" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LessonDetailModal isOpen={modalState === 'menu'} lessonId={menuLessonId}
        onClose={() => stateHundler('none')} onSaved={onModalRefresh}
        onParticipantsChanged={onModalRefresh} />
    </div>
  );
}
